import React, { useEffect, useState, useCallback } from 'react';
import { TaskColumn } from './TaskColumn';
import { DragAndDropProvider } from '../DragAndDrop/DragAndDropProvider';
import { QuickAddModal } from '../QuickAdd/QuickAddModal';
import { TaskTypeManager } from '../../../task-types/components/TaskTypeManager';
import { TaskTypeFilter } from '../TaskTypeFilter';
import { TaskSearchModal } from '../../../../components/ui/TaskSearchModal';
import { GoalSidebar } from '../../../goals/components/GoalSidebar';
import { GoalDetailModal } from '../../../goals/components/GoalDetailModal';
import { PausedGoalTaskPanel } from '../../../goals/components/PausedGoalTaskPanel';
import { GoalCompletionModal } from '../../../goals/components/GoalCompletionModal';
import { useTaskStore } from '../../../../stores/taskStore';
import { useAuthStore } from '../../../../stores/authStore';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';
import { useGoalStore } from '../../../../stores/goalStore';
import type { TaskStatus } from '../../../../types';
import { BillingBanner } from '../../../billing/BillingBanner';
import { EmailBindBanner } from '../../../auth/components/EmailBindBanner';

interface TaskBoardProps {
  onAddTask?: (status: TaskStatus) => void;
  showTaskTypeManager: boolean;
  setShowTaskTypeManager: (show: boolean) => void;
  showGoalSidebar: boolean;
  setShowGoalSidebar: (show: boolean) => void;
  showPausedGoalTasks: boolean;
  setShowPausedGoalTasks: (show: boolean) => void;
  selectedPausedGoalId: string | null;
  pausedGoalTargetElement: HTMLElement | null;
  onPausedGoalClick: (goalId: string, targetElement?: HTMLElement) => void;
  onGoalSelect: (goalId: string | null) => void;
  onClosePausedPanel?: () => void;
}

export const TaskBoard: React.FC<TaskBoardProps> = ({
  onAddTask,
  showTaskTypeManager,
  setShowTaskTypeManager,
  showGoalSidebar,
  setShowGoalSidebar,
  showPausedGoalTasks,
  setShowPausedGoalTasks,
  selectedPausedGoalId,
  pausedGoalTargetElement,
  onPausedGoalClick,
  onGoalSelect,
  onClosePausedPanel
}) => {
  const tasks = useTaskStore(state => state.tasks);
  const taskTypes = useTaskStore(state => state.taskTypes);
  const teamMembers = useTaskStore(state => state.teamMembers);
  const selectedTaskTypeId = useTaskStore(state => state.selectedTaskTypeId);
  const selectedGoalId = useTaskStore(state => state.selectedGoalId);
  const selectedMemberIds = useTaskStore(state => state.selectedMemberIds);
  const setSelectedTaskTypeId = useTaskStore(state => state.setSelectedTaskTypeId);
  const setSelectedGoalId = useTaskStore(state => state.setSelectedGoalId);
  const setSelectedMemberId = useTaskStore(state => state.setSelectedMemberId);
  const getFilteredTasksByStatus = useTaskStore(state => state.getFilteredTasksByStatus);
  const getFilteredTasksByStatusAndMember = useTaskStore(state => state.getFilteredTasksByStatusAndMember);
  const loading = useTaskStore(state => state.loading);
  const error = useTaskStore(state => state.error);
  const clearError = useTaskStore(state => state.clearError);
  const loadTeamMembers = useTaskStore(state => state.loadTeamMembers);
  const { team } = useAuthStore();
  const { currentWorkspace } = useWorkspaceStore();
  const { goalCompletionModal, confirmGoalCompletion, closeGoalCompletionModal } = useGoalStore();

  // 弹窗状态
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showGoalDetail, setShowGoalDetail] = useState(false);
  const [selectedGoalDetailId, setSelectedGoalDetailId] = useState<string | 'new'>('new');
  const [selectedParentGoalId, setSelectedParentGoalId] = useState<string | undefined>(undefined);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // 组件挂载时加载数据
  useEffect(() => {
    const loadData = async () => {
      try {
        const promises = [
          useTaskStore.getState().loadTasks(),
          useTaskStore.getState().loadTaskTypes()
        ];
        
        // 如果是团队工作空间，加载团队成员
        if (currentWorkspace.type === 'team' && currentWorkspace.teamId) {
          promises.push(loadTeamMembers(currentWorkspace.teamId));
        }
        
        await Promise.all(promises);
      } finally {
        setInitialLoaded(true);
      }
    };
    loadData();
  }, [currentWorkspace.type, currentWorkspace.teamId, loadTeamMembers]); // 依赖工作空间类型和团队ID

  // 全局键盘快捷键监听
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+I 新建任务
      if (e.ctrlKey && e.key === 'i') {
        e.preventDefault(); // 阻止浏览器默认行为
        setShowQuickAdd(true);
      }
      // Ctrl+F 搜索任务
      if (e.ctrlKey && e.key === 'f') {
        e.preventDefault(); // 阻止浏览器默认搜索行为
        setShowSearch(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // 清理监听器
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // 清除错误
  const handleClearError = () => {
    clearError();
  };

  // 判断是否为团队工作空间
  const isTeamWorkspace = currentWorkspace.type === 'team';

  // 团队成员过滤处理
  const handleMemberSelect = (status: 'IN_PROGRESS' | 'DONE', memberId: string | null) => {
    setSelectedMemberId(status, memberId);
  };

  // 检查是否有任何过滤器正在显示
  const hasVisibleFilter = isTeamWorkspace && teamMembers.length > 0;

  // 「受阻」列只在有受阻任务时才出现，平时看板保持四列
  const blockedTasks = getFilteredTasksByStatusAndMember('BLOCKED');
  const showBlocked = blockedTasks.length > 0;


  // 只在初始加载时显示加载页面
  if (!initialLoaded) {
    return (
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-600">加载任务数据中...</span>
          </div>
        </div>
      </div>
    );
  }

  // 处理目标详情
  const handleGoalDetail = (goalId: string | 'new', parentGoalId?: string) => {
    setSelectedGoalDetailId(goalId);
    setSelectedParentGoalId(parentGoalId);
    setShowGoalDetail(true);
  };

  return (
    <>
      {/* 拖拽上下文包装整个区域 */}
      <DragAndDropProvider showGoalSidebar={showGoalSidebar}>
        {/* 目标侧栏 */}
        <GoalSidebar
          isOpen={showGoalSidebar}
          onClose={() => setShowGoalSidebar(false)}
          selectedGoalId={selectedGoalId}
          onGoalSelect={(goalId) => {
            setSelectedGoalId(goalId);
            onGoalSelect(goalId);
          }}
          onGoalDetail={handleGoalDetail}
          onPausedGoalClick={onPausedGoalClick}
          onClosePausedPanel={onClosePausedPanel}
        />

        <main className={`max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8 pb-20 transition-all duration-300 ${
          showGoalSidebar ? 'ml-96' : ''
        } ${
          showPausedGoalTasks ? 'mr-80' : ''
        }`}>
        <div className="flex gap-6">
          {/* 主内容区域：min-w-0 才允许内部看板横向滚动，而不是把整页撑宽 */}
          <div className="flex-1 min-w-0">
            <EmailBindBanner />

            {/* 团队超出免费人数且席位到期时的提示（宽限中 / 只读） */}
            {isTeamWorkspace && currentWorkspace.teamId && <BillingBanner teamId={currentWorkspace.teamId} />}

            {/* 错误提示 */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded-full mr-3"></div>
                  <span className="text-red-700">{error}</span>
                </div>
                <button
                  onClick={handleClearError}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  关闭
                </button>
              </div>
            )}

            {/* 任务看板 */}
            {/* 所有列永远排在同一行：宽度够就平分，不够就在看板里横向滚动（不折行）。
                两侧留白是给左侧「全部」标签和右侧成员筛选的位置 */}
            <div className="overflow-x-auto -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10 pb-3">
            <div
              className="grid gap-6 compact-view"
              style={{ gridAutoFlow: 'column', gridAutoColumns: 'minmax(250px, 1fr)' }}
            >
          {/* 待办任务列带过滤器 */}
          <div className="relative">
            <TaskTypeFilter
              taskTypes={taskTypes}
              selectedTypeId={selectedTaskTypeId}
              onTypeSelect={setSelectedTaskTypeId}
            />
            <TaskColumn
              status="TODO"
              title="待办任务"
              tasks={tasks}
              taskTypes={taskTypes}
              filteredTasks={getFilteredTasksByStatus('TODO')}
              isCompact={true}
            />
          </div>

          <TaskColumn
            status="IN_PROGRESS"
            title="进行中"
            tasks={tasks}
            taskTypes={taskTypes}
            filteredTasks={getFilteredTasksByStatusAndMember('IN_PROGRESS')}
            isCompact={true}
            teamMembers={teamMembers}
            selectedMemberId={selectedMemberIds.IN_PROGRESS}
            onMemberSelect={(memberId) => handleMemberSelect('IN_PROGRESS', memberId)}
            isTeamWorkspace={isTeamWorkspace}
          />

          <TaskColumn
            status="REVIEW"
            title="待验收"
            tasks={tasks}
            taskTypes={taskTypes}
            filteredTasks={getFilteredTasksByStatusAndMember('REVIEW')}
            isCompact={true}
          />

          {showBlocked && (
            <TaskColumn
              status="BLOCKED"
              title="受阻"
              tasks={tasks}
              taskTypes={taskTypes}
              filteredTasks={blockedTasks}
              isCompact={true}
            />
          )}

          <TaskColumn
            status="DONE"
            title="已完成"
            tasks={tasks}
            taskTypes={taskTypes}
            filteredTasks={getFilteredTasksByStatusAndMember('DONE')}
            isCompact={true}
            teamMembers={teamMembers}
            selectedMemberId={selectedMemberIds.DONE}
            onMemberSelect={(memberId) => handleMemberSelect('DONE', memberId)}
            isTeamWorkspace={isTeamWorkspace}
          />
            </div>
            </div>

            {/* 全局快速添加任务弹窗 */}
            <QuickAddModal
              isOpen={showQuickAdd}
              onClose={() => setShowQuickAdd(false)}
              status="TODO"
            />

            {/* 任务搜索弹窗 */}
            <TaskSearchModal
              isOpen={showSearch}
              onClose={() => setShowSearch(false)}
              tasks={tasks}
              taskTypes={taskTypes}
            />
          </div>

        </div>
      </main>

      {/* 目标详情弹出框 */}
      <GoalDetailModal
        isOpen={showGoalDetail}
        onClose={() => setShowGoalDetail(false)}
        goalId={selectedGoalDetailId}
        parentGoalId={selectedParentGoalId}
      />

      {/* 快捷键提示 - 右侧浮动面板 */}
      <div className="hidden min-[1880px]:block fixed right-4 top-1/2 transform -translate-y-1/2 z-40">
        <div className="bg-neutral-25 rounded-lg shadow-lg border border-neutral-100 p-3 space-y-2 text-xs">
          <div className="text-gray-400 font-medium mb-2">操作提示</div>
          <div className="flex items-center gap-1.5 text-gray-600">
            <div className="flex items-center gap-0.5">
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">⌃</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">F</kbd>
            </div>
            <span>搜索</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-600">
            <div className="flex items-center gap-0.5">
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">⌃</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">I</kbd>
            </div>
            <span>新建</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-600">
            <div className="flex items-center gap-0.5">
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">⌃</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">B</kbd>
            </div>
            <span>目标管理</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-600">
            <div className="flex items-center">
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">点击</kbd>
            </div>
            <span>打开任务</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-600">
            <div className="flex items-center">
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">ESC</kbd>
            </div>
            <span>取消编辑</span>
          </div>
        </div>
      </div>

      {/* 任务类型管理器 */}
      <TaskTypeManager
        isOpen={showTaskTypeManager}
        onClose={() => setShowTaskTypeManager(false)}
      />

      {/* 暂停目标任务面板 */}
      <PausedGoalTaskPanel
        isOpen={showPausedGoalTasks}
        onClose={() => setShowPausedGoalTasks(false)}
        goalId={selectedPausedGoalId}
        targetElement={pausedGoalTargetElement}
      />

      {/* 目标完成确认模态框 */}
      <GoalCompletionModal
        isOpen={goalCompletionModal.isOpen}
        onClose={closeGoalCompletionModal}
        onConfirm={confirmGoalCompletion}
        goalTitle={goalCompletionModal.goalTitle}
      />
      </DragAndDropProvider>
    </>
  );
};